/**
 * Helpers for crawlable list pagination URLs: {listPath}/p/{n}.
 * Page 1 still uses /p/1 in hrefs; canonical for page 1 stays the bare list path.
 * Sort is stored as ?sort= when it differs from the product default.
 */

const PAGE_SEGMENT_RE = /\/p\/(\d+)\/?$/i;

/** Remove a trailing /p/{n} segment from a pathname. */
export function stripPageSegment(pathname: string): string {
  const logdetails: string[] = [];
  logdetails.push("action=stripPageSegment");
  logdetails.push(`pathname=${pathname}`);
  const base = (pathname || "/").replace(PAGE_SEGMENT_RE, "") || "/";
  // Avoid leaving a trailing slash except for root
  const normalized = base.length > 1 && base.endsWith("/") ? base.slice(0, -1) : base;
  logdetails.push(`basePath=${normalized}`);
  console.log("listPagePath ->", logdetails.join(" | "));
  return normalized;
}

/**
 * Build a page-wise list href: {base}/p/{page} plus optional query string.
 * Always includes /p/{n}, including page 1. Preserves existing search (e.g. ?sort=2).
 */
export function buildListPageHref(
  pathname: string,
  page: number,
  search?: string
): string {
  const logdetails: string[] = [];
  logdetails.push("action=buildListPageHref");
  logdetails.push(`pathname=${pathname}`);
  logdetails.push(`page=${page}`);
  logdetails.push(`search=${search || ""}`);
  const base = stripPageSegmentSilent(pathname);
  const safePage = Math.max(1, Math.floor(Number(page) || 1));
  const path = `${base}/p/${safePage}`;
  const query = normalizeSearch(search);
  const href = query ? `${path}${query}` : path;
  logdetails.push(`href=${href}`);
  console.log("listPagePath ->", logdetails.join(" | "));
  return href;
}

/** Parse list pathname into base path and 1-based page number. */
export function parseListPage(pathname: string): { basePath: string; page: number } {
  const logdetails: string[] = [];
  logdetails.push("action=parseListPage");
  logdetails.push(`pathname=${pathname}`);
  const match = (pathname || "").match(PAGE_SEGMENT_RE);
  const page = match ? Math.max(1, parseInt(match[1], 10) || 1) : 1;
  const basePath = stripPageSegmentSilent(pathname);
  logdetails.push(`basePath=${basePath}`);
  logdetails.push(`page=${page}`);
  console.log("listPagePath ->", logdetails.join(" | "));
  return { basePath, page };
}

/**
 * Read ?sort= from a search string, falling back to the product default.
 */
export function readSortParam(search: string | undefined, defaultSort: string): string {
  const logdetails: string[] = [];
  logdetails.push("action=readSortParam");
  logdetails.push(`search=${search || ""}`);
  logdetails.push(`defaultSort=${defaultSort}`);
  try {
    const params = new URLSearchParams(
      (search || "").startsWith("?") ? (search || "").slice(1) : search || ""
    );
    const raw = params.get("sort");
    if (raw !== null && raw !== "") {
      logdetails.push(`sort=${raw}`);
      console.log("listPagePath ->", logdetails.join(" | "));
      return raw;
    }
  } catch {
    logdetails.push("result=parse_error");
  }
  logdetails.push(`sort=${defaultSort}`);
  console.log("listPagePath ->", logdetails.join(" | "));
  return defaultSort;
}

/**
 * Build pathname + search with sort applied.
 * Omits ?sort= when the value equals the product default so canonical URLs stay clean.
 * Leaves other query keys alone.
 */
export function withSortQuery(
  pathname: string,
  search: string | undefined,
  sort: string,
  defaultSort: string
): string {
  const logdetails: string[] = [];
  logdetails.push("action=withSortQuery");
  logdetails.push(`pathname=${pathname}`);
  logdetails.push(`search=${search || ""}`);
  logdetails.push(`sort=${sort}`);
  logdetails.push(`defaultSort=${defaultSort}`);

  const params = new URLSearchParams(
    (search || "").startsWith("?") ? (search || "").slice(1) : search || ""
  );
  if (!sort || sort === defaultSort) {
    params.delete("sort");
  } else {
    params.set("sort", sort);
  }
  const query = params.toString();
  const href = query ? `${pathname}?${query}` : pathname;
  logdetails.push(`href=${href}`);
  console.log("listPagePath ->", logdetails.join(" | "));
  return href;
}

/**
 * Build the list return path (pathname + search) to pass into detail navigation state.
 * Used so breadcrumbs and Back restore page, sort, and filters.
 */
export function buildListReturnPath(pathname: string, search?: string): string {
  const logdetails: string[] = [];
  logdetails.push("action=buildListReturnPath");
  logdetails.push(`pathname=${pathname}`);
  logdetails.push(`search=${search || ""}`);
  const query = normalizeSearch(search);
  const href = query ? `${pathname}${query}` : pathname;
  logdetails.push(`href=${href}`);
  console.log("listPagePath ->", logdetails.join(" | "));
  return href;
}

/** True when the current URL has a non-empty ?sort= value. */
export function hasSortQuery(search: string | undefined): boolean {
  try {
    const params = new URLSearchParams(
      (search || "").startsWith("?") ? (search || "").slice(1) : search || ""
    );
    const raw = params.get("sort");
    return raw !== null && raw !== "";
  } catch {
    return false;
  }
}

/** Strip without logging (used internally to avoid double logs). */
function stripPageSegmentSilent(pathname: string): string {
  const base = (pathname || "/").replace(PAGE_SEGMENT_RE, "") || "/";
  return base.length > 1 && base.endsWith("/") ? base.slice(0, -1) : base;
}

/** Normalize a search string to "?key=value" or "". */
function normalizeSearch(search?: string): string {
  if (!search) return "";
  const trimmed = search.trim();
  if (!trimmed || trimmed === "?") return "";
  return trimmed.startsWith("?") ? trimmed : `?${trimmed}`;
}
